/**
 * ParentSelectButton — `.parent-select` from the prototype
 * (cms-redesign-mockup.html ~4364–4465). A custom button that shows
 * the currently-attached parent as a colored type-pill + name, with
 * an inline grouped + searchable dropdown.
 *
 *   <ParentSelectButton
 *     value={formData.associatedParent}    // "42CAT" | "15SUB" | … | "0"
 *     onChange={(value) => setField('associatedParent', value)}
 *     allowedTypes={['category', 'subcategory', 'challenge', 'series', 'audiobook']}
 *     showQuickTaps={false}
 *   />
 *
 * Value format: matches v1 ParentSelector — "<id><TYPE>" suffix where
 * TYPE is one of CAT / SUB / SER / CHA / AUD, or "0" for unparented.
 * The Session edit form's submit handler already splits this back out
 * into category_id / subcategory_id / series_id / challenge_id on
 * save, so the value contract here is byte-identical to v1.
 *
 * Data: pulls categories / subcategories / challenges / series from
 * the same Redux slices the v1 ParentSelector uses. Doesn't fetch
 * here — the surrounding form has already triggered the fetches as
 * part of its load step (see AddEditSessionV2's loadData useEffect).
 *
 * Behavior:
 *   • Click → dropdown opens, search input focuses.
 *   • Substring search across name across all groups.
 *   • Subcategories rendered indented underneath their parent
 *     category (matches v1 + prototype). Orphaned subcategories sit
 *     in their own group.
 *   • Audiobooks (subcategories with subcategory_is_book=1) go in
 *     the Audiobooks group with the book-tinted pill.
 *   • Outside click + Escape close. Active row highlighted.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { cn } from '../cn';
import BrandIcon from '../BrandIcon';

/* ─── Type → pill / glyph palette (matches prototype's
 *  .parent-pill-* and .parent-option.is-* tints).
 *
 *  `icon` is the brand-icon key from `brandIcons.js` — same set the
 *  sidebar nav uses for Categories / Subcategories / Series /
 *  Challenges / Audiobooks. Keeping the iconography consistent
 *  between the menu and the parent picker makes the type pill
 *  immediately recognizable. `glyph` is the fallback character for
 *  the "none" row where there's no nav-equivalent icon. */
const TYPE_META = {
  cat:  { label: 'Category',    bg: '#75A29333', color: '#5C7E72', icon: 'categories' },
  sub:  { label: 'Subcategory', bg: '#C4715B33', color: '#C4715B', icon: 'subcategory' },
  ser:  { label: 'Series',      bg: '#1AA6B714', color: '#1A8A98', icon: 'series' },
  cha:  { label: 'Challenge',   bg: '#E58A6A33', color: '#C4715B', icon: 'challenges' },
  book: { label: 'Audiobook',   bg: '#2C3E5012', color: 'rgb(var(--color-navy))', icon: 'audiobooks' },
  none: { label: 'None',        bg: '#EFE7D8',   color: '#9CA1A8', glyph: '∅' },
};

// v1's value suffix → TYPE_META key. v1 uses "AUD" for audiobooks
// but the prototype calls them `book` — map between them so the
// pill colors line up with the prototype's design intent.
const SUFFIX_TO_TYPE = {
  CAT: 'cat',
  SUB: 'sub',
  SER: 'ser',
  CHA: 'cha',
  AUD: 'book',
};

function parseValue(value) {
  if (!value || value === '0') return { type: 'none', id: 0 };
  const match = /^(\d+)([A-Z]+)$/.exec(String(value));
  if (!match) return { type: 'none', id: 0 };
  const [, id, suffix] = match;
  return { type: SUFFIX_TO_TYPE[suffix] || 'none', id: Number(id), suffix };
}

/* ─── Pill renderer ─────────────────────────────────────────────────── */

function ParentPill({ type, label }) {
  const meta = TYPE_META[type] || TYPE_META.none;
  return (
    <span
      className="inline-flex items-center font-condensed font-bold uppercase whitespace-nowrap"
      style={{
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        fontSize: 10.5,
        letterSpacing: '0.08em',
        background: meta.bg,
        color: meta.color,
        flexShrink: 0,
      }}
    >
      {meta.icon ? (
        <BrandIcon name={meta.icon} size={12} />
      ) : (
        <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>{meta.glyph}</span>
      )}
      {label || meta.label}
    </span>
  );
}

/* ─── Option row glyph (smaller, square-rounded badge) ──────────────── */

function OptionGlyph({ type }) {
  const meta = TYPE_META[type] || TYPE_META.none;
  return (
    <span
      aria-hidden
      className="grid place-items-center"
      style={{
        width: 22,
        height: 22,
        borderRadius: 4,
        background: meta.bg,
        color: meta.color,
        fontSize: 12,
        flexShrink: 0,
      }}
    >
      {meta.icon ? (
        <BrandIcon name={meta.icon} size={14} />
      ) : (
        meta.glyph
      )}
    </span>
  );
}

/* ─── Build options from Redux (mirrors v1 ParentSelector logic) ──── */

function useParentOptions({ allowedTypes, showQuickTaps }) {
  const rawCategories = useSelector((s) => s.categories.all_categories);
  const rawSubcategories = useSelector((s) => s.subcategories.subcategories);
  const rawChallenges = useSelector((s) => s.challenges.challenges);
  const rawSeries = useSelector((s) => s.series.all_series);

  return useMemo(() => {
    const cats = Array.isArray(rawCategories) ? rawCategories : [];
    const subs = rawSubcategories ? Object.values(rawSubcategories) : [];
    const challenges = rawChallenges ? Object.values(rawChallenges) : [];
    const series = !rawSeries
      ? []
      : (Array.isArray(rawSeries) ? rawSeries : Object.values(rawSeries))
          .filter((sr) => sr && sr.series_id && sr.series_title);

    const opts = [];

    // None
    opts.push({
      key: '0',
      value: '0',
      type: 'none',
      group: 'None',
      label: 'None',
      meta: 'standalone',
      indent: false,
      active: false,
      disabled: false,
    });

    const wantCat = allowedTypes.includes('category');
    const wantSub = allowedTypes.includes('subcategory');
    const wantCha = allowedTypes.includes('challenge');
    const wantSer = allowedTypes.includes('series');
    const wantBook = allowedTypes.includes('audiobook');

    // Categories + their subcategories. Mirror v1 filter rules:
    // exclude audiobook categories from the regular category list,
    // and respect the showQuickTaps flag in both directions.
    const visibleCats = cats.filter((c) => {
      if (!wantCat) return false;
      if (!showQuickTaps && c.category_is_quick_tap) return false;
      if (showQuickTaps && !c.category_is_quick_tap) return false;
      if (c.category_is_audiobook) return false;
      return true;
    });

    const seenSubIds = new Set();
    visibleCats.forEach((c) => {
      opts.push({
        key: `${c.category_id}CAT`,
        value: `${c.category_id}CAT`,
        type: 'cat',
        group: 'Categories',
        label:
          (c.category_title || 'Untitled')
          + (c.category_is_quick_tap ? ' (Quick Tap)' : '')
          + (!c.category_is_active ? ' (Inactive)' : ''),
        meta: 'CAT',
        indent: false,
        active: false,
        disabled: !c.category_is_active,
      });
      if (!wantSub) return;
      subs
        .filter((sc) =>
          sc.category_id === c.category_id
          && !sc.subcategory_is_book
          && (showQuickTaps ? sc.subcategory_is_quick_tap : !sc.subcategory_is_quick_tap),
        )
        .forEach((sc) => {
          seenSubIds.add(sc.subcategory_id);
          opts.push({
            key: `${sc.subcategory_id}SUB`,
            value: `${sc.subcategory_id}SUB`,
            type: 'sub',
            group: 'Categories',
            label:
              (sc.subcategory_title || 'Untitled')
              + (sc.subcategory_is_quick_tap ? ' (Quick Tap)' : '')
              + (!sc.subcategory_is_active ? ' (Inactive)' : ''),
            meta: 'SUB',
            indent: true,
            active: false,
            disabled: !sc.subcategory_is_active,
          });
        });
    });

    // Orphaned subcategories (no category_id, not a book, not yet shown).
    if (wantSub) {
      subs
        .filter((sc) => {
          if (sc.subcategory_is_book) return false;
          if (showQuickTaps ? !sc.subcategory_is_quick_tap : sc.subcategory_is_quick_tap) return false;
          const orphan = !sc.category_id || sc.category_id === 0;
          return orphan && !seenSubIds.has(sc.subcategory_id);
        })
        .forEach((sc) => {
          opts.push({
            key: `${sc.subcategory_id}SUB`,
            value: `${sc.subcategory_id}SUB`,
            type: 'sub',
            group: 'Orphaned subcategories',
            label:
              (sc.subcategory_title || 'Untitled')
              + (sc.subcategory_is_quick_tap ? ' (Quick Tap)' : '')
              + (!sc.subcategory_is_active ? ' (Inactive)' : ''),
            meta: 'SUB',
            indent: false,
            active: false,
            disabled: !sc.subcategory_is_active,
          });
        });
    }

    // Audiobooks (subcategories where subcategory_is_book = 1)
    if (wantBook) {
      subs
        .filter((sc) => {
          if (!sc.subcategory_is_book) return false;
          if (!showQuickTaps && sc.subcategory_is_quick_tap) return false;
          return true;
        })
        .forEach((sc) => {
          opts.push({
            key: `${sc.subcategory_id}AUD`,
            value: `${sc.subcategory_id}AUD`,
            type: 'book',
            group: 'Audiobooks',
            label: sc.subcategory_title || 'Untitled',
            meta: 'BOOK',
            indent: false,
            active: false,
            disabled: false,
          });
        });
    }

    if (wantCha) {
      challenges.forEach((ch) => {
        opts.push({
          key: `${ch.challenge_id}CHA`,
          value: `${ch.challenge_id}CHA`,
          type: 'cha',
          group: 'Challenges',
          label: ch.challenge_title || 'Untitled',
          meta: 'CHA',
          indent: false,
          active: false,
          disabled: false,
        });
      });
    }

    if (wantSer) {
      series.forEach((sr) => {
        opts.push({
          key: `${sr.series_id}SER`,
          value: `${sr.series_id}SER`,
          type: 'ser',
          group: 'Series',
          label: sr.series_title || 'Untitled',
          meta: 'SER',
          indent: false,
          active: false,
          disabled: false,
        });
      });
    }

    return opts;
  }, [rawCategories, rawSubcategories, rawChallenges, rawSeries, allowedTypes, showQuickTaps]);
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function ParentSelectButton({
  value,
  onChange,
  allowedTypes = ['category', 'subcategory', 'challenge', 'series', 'audiobook'],
  showQuickTaps = false,
  className,
}) {
  const options = useParentOptions({ allowedTypes, showQuickTaps });
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value],
  );

  // Group filtered options for rendering. We preserve the source
  // order so the user sees categories in their CMS-sort order,
  // subcategories indented beneath, etc.
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? options.filter((o) => o.label.toLowerCase().includes(q))
      : options;
    const order = [];
    const map = new Map();
    for (const o of filtered) {
      if (!map.has(o.group)) {
        map.set(o.group, []);
        order.push(o.group);
      }
      map.get(o.group).push(o);
    }
    return order.map((group) => ({ group, items: map.get(group) }));
  }, [options, query]);

  const selectedType = selected ? selected.type : 'none';
  const selectedLabel = selected
    ? (selected.type === 'none' ? null : selected.label)
    : null;

  return (
    <div ref={wrapperRef} className={cn('relative', className)} style={{ minWidth: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center cursor-pointer w-full"
        style={{
          gap: 12,
          padding: '8px 14px 8px 8px',
          background: 'rgb(var(--color-cream))',
          border: '1px solid rgb(var(--color-sand))',
          borderRadius: 8,
          fontFamily: 'inherit',
          textAlign: 'left',
          transition: 'all 160ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))';
          e.currentTarget.style.background = 'rgb(var(--color-surface))';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgb(var(--color-sand))';
          e.currentTarget.style.background = 'rgb(var(--color-cream))';
        }}
      >
        <ParentPill type={selectedType} />
        <span
          className={cn(
            'flex-1 min-w-0 truncate',
            selectedLabel ? 'text-navy' : 'text-text-muted italic',
          )}
          style={{ fontSize: 14, fontWeight: selectedLabel ? 700 : 400 }}
        >
          {selectedLabel || 'Pick a parent…'}
        </span>
        <span className="text-text-muted" style={{ fontSize: 11, flexShrink: 0 }}>▾</span>
      </button>

      {open && (
        <div
          className="absolute"
          style={{
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: 'rgb(var(--color-surface))',
            border: '1px solid rgb(var(--color-sand))',
            borderRadius: 8,
            boxShadow: '0 8px 24px #2C3E5018',
            overflow: 'hidden',
            zIndex: 30,
          }}
        >
          <div
            className="flex items-center"
            style={{
              gap: 8,
              padding: '10px 12px',
              borderBottom: '1px solid #EFE7D8',
              background: 'rgb(var(--color-cream))',
            }}
          >
            <span aria-hidden className="text-text-muted" style={{ fontSize: 13, flexShrink: 0 }}>⌕</span>
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search categories, subcategories, series…"
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                background: 'transparent',
                outline: 'none',
                fontFamily: 'inherit',
                fontSize: 13,
                color: 'rgb(var(--color-navy))',
              }}
            />
          </div>

          <div style={{ maxHeight: 320, overflowY: 'auto', padding: '6px 0' }}>
            {grouped.length === 0 ? (
              <div
                className="text-text-muted italic"
                style={{ padding: '12px 14px', fontSize: 13 }}
              >
                No parents match "{query}".
              </div>
            ) : (
              grouped.map(({ group, items }) => (
                <React.Fragment key={group}>
                  <div
                    className="font-condensed font-bold uppercase text-text-muted"
                    style={{
                      fontSize: 10,
                      letterSpacing: '0.1em',
                      padding: '10px 14px 4px',
                    }}
                  >
                    {group}
                  </div>
                  {items.map((o) => {
                    const isActive = o.value === value;
                    return (
                      <button
                        key={o.key}
                        type="button"
                        disabled={o.disabled}
                        onClick={() => {
                          onChange?.(o.value);
                          setOpen(false);
                        }}
                        className={cn(
                          'w-full flex items-center cursor-pointer',
                          'disabled:opacity-60 disabled:cursor-not-allowed disabled:italic',
                        )}
                        style={{
                          gap: 10,
                          padding: o.indent ? '8px 14px 8px 38px' : '8px 14px',
                          fontSize: 13,
                          color: isActive ? '#5C7E72' : 'rgb(var(--color-navy))',
                          fontWeight: isActive ? 600 : 400,
                          background: isActive ? '#75A29333' : 'transparent',
                          border: 0,
                          textAlign: 'left',
                          fontFamily: 'inherit',
                          transition: 'background 120ms ease',
                        }}
                        onMouseEnter={(e) => {
                          if (isActive || o.disabled) return;
                          e.currentTarget.style.background = 'rgb(var(--color-cream))';
                        }}
                        onMouseLeave={(e) => {
                          if (isActive) return;
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <OptionGlyph type={o.type} />
                        <span className="flex-1 min-w-0 truncate" style={{ textAlign: 'left' }}>
                          {o.label}
                        </span>
                        {o.meta && (
                          <span
                            className="font-mono text-text-muted"
                            style={{ fontSize: 10.5, marginLeft: 'auto' }}
                          >
                            {o.meta}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
